import { isSet, setLookupMappingFieldValue } from '../../../../Utils/CommonFunctions';
import { createNewSubformRow, extractFieldValue, getSubformIndex } from './subformHelpers';

/**
 * Factory function to create add row handler
 * @param {Object} params - Handler parameters
 * @param {Object} params.section - Section object
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {String} params.modType - Module type
 * @param {Function} params.setSubFormDataAction - Redux action to update subform data
 * @returns {Function} Add row handler function
 */
export const createAddRowHandler = ({ section, subformData, subformIndex, modType, setSubFormDataAction }) => {
    return () => {
        if (section && section.data && subformData && subformData[subformIndex] !== undefined) {
            const currentData = subformData[subformIndex];
            const newRow = createNewSubformRow(section.data, currentData.subform_rows.length);
            
            // Match SubformTypeForm: set isOpenBox to true after creating row (for manual add)
            // In SubformTypeForm's addNewSubForm, isOpenBox is set after push
            const addedRowIndex = currentData.subform_rows.length;
            newRow.isOpenBox = true;
            
            const updatedSubformData = [...subformData];
            updatedSubformData[subformIndex] = {
                ...currentData,
                subform_rows: [...currentData.subform_rows, newRow]
            };
            
            if (setSubFormDataAction) {
                setSubFormDataAction(modType, updatedSubformData);
            }
        }
    };
};

/**
 * Factory function to create bulk delete handler
 * @param {Object} params - Handler parameters
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {Object} params.currentSectionSubformData - Current section subform data
 * @param {String} params.modType - Module type
 * @param {Function} params.setSubFormDataAction - Redux action
 * @param {Function} params.clearSubformValidation - Clear validation function
 * @param {Function} params.addRowHandler - Add row handler (for adding empty row after full delete)
 * @returns {Function} Bulk delete handler function
 */
export const createBulkDeleteHandler = ({ 
    subformData, 
    subformIndex, 
    currentSectionSubformData, 
    modType, 
    setSubFormDataAction, 
    clearSubformValidation,
    addRowHandler 
}) => {
    return async () => {
        if (subformData && subformData[subformIndex] !== undefined) {
            const selecteddata = currentSectionSubformData.subform_rows.filter((item) => item.is_selected === true);
            
            if (currentSectionSubformData.subform_rows.length === selecteddata.length) {
                // If all rows are selected, clear all and add a new empty row
                selecteddata.forEach((val, rowIndex) => {
                    if (clearSubformValidation) {
                        clearSubformValidation(modType, rowIndex);
                    }
                });
                const updatedSubformData = [...subformData];
                updatedSubformData[subformIndex] = {
                    ...updatedSubformData[subformIndex],
                    subform_rows: []
                };
                if (setSubFormDataAction) {
                    await setSubFormDataAction(modType, updatedSubformData);
                }
                // Add new empty row after deletion
                if (addRowHandler) {
                    addRowHandler();
                }
            } else if (selecteddata.length > 0) {
                // Delete only selected rows
                const updateddata = currentSectionSubformData.subform_rows.filter((item) => item.is_selected === false);
                updateddata.forEach((val, rowIndex) => {
                    if (clearSubformValidation) {
                        clearSubformValidation(modType, rowIndex);
                    }
                });
                const updatedSubformData = [...subformData];
                updatedSubformData[subformIndex] = {
                    ...updatedSubformData[subformIndex],
                    subform_rows: updateddata
                };
                if (setSubFormDataAction) {
                    setSubFormDataAction(modType, updatedSubformData);
                }
            }
        }
    };
};

/**
 * Factory function to create bulk edit change handler
 * @param {Function} setBulkEditItem - Set bulk edit item state
 * @param {Function} setBulkEditPayload - Set bulk edit payload state
 * @param {Object} currentPayload - Current payload state
 * @returns {Function} Bulk edit change handler
 */
export const createBulkEditChangeHandler = (setBulkEditItem, setBulkEditPayload, currentPayload) => {
    return (updateData, e, inputType = 'text') => {
        const fieldValue = extractFieldValue(e, inputType);
        
        setBulkEditItem((prev) => ({ ...prev, selectedValue: fieldValue }));
        setBulkEditPayload({
            ...currentPayload,
            value: fieldValue,
            inputType: inputType,
            updateData: updateData
        });
    };
};

/**
 * Factory function to create bulk edit save handler
 * @param {Object} params - Handler parameters
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {Object} params.bulkEditPayload - Bulk edit payload
 * @param {String} params.modType - Module type
 * @param {Function} params.setSubFormDataAction - Redux action
 * @param {Array} params.fieldGroups - Field groups array
 * @param {Object} params.defProps - Default props
 * @param {Function} params.closeBulkEditPopup - Close popup function
 * @returns {Function} Bulk edit save handler
 */
export const createBulkEditSaveHandler = ({ 
    subformData, 
    subformIndex, 
    bulkEditPayload, 
    modType, 
    setSubFormDataAction,
    fieldGroups,
    defProps,
    closeBulkEditPopup
}) => {
    return () => {
        if (!subformData || subformData[subformIndex] === undefined) return;
        
        let updateddata = subformData[subformIndex].subform_rows.map((obj) => {
            let newdata = [];
            let fieldValue = bulkEditPayload.value;
            
            if (bulkEditPayload.inputType === 'Lookup' && obj.is_selected === true) {
                let tabescriptorList = [];
                fieldGroups.forEach((section) => {
                    isSet(section.data, []).forEach((ite) => {
                        tabescriptorList.push(ite);
                    });
                });
                const lookupMappingDataa = setLookupMappingFieldValue(
                    obj.subform_column_values,
                    bulkEditPayload.updateData,
                    defProps.lookupDependency,
                    "subform",
                    tabescriptorList
                );
                
                newdata = lookupMappingDataa.updatedPreviewItems.map((val) => {
                    if (val.table_descriptor_id === bulkEditPayload.type) {
                        val.dpnm = fieldValue?.displayValue;
                        val.selected_value = isSet(fieldValue.selectedValue, fieldValue);
                        val.overridden = true;
                    }
                    return val;
                });
            } else {
                newdata = obj.subform_column_values;
            }
            
            return {
                ...obj,
                is_selected: false,
                subform_column_values: bulkEditPayload.inputType === 'Lookup' ? newdata
                    : obj.subform_column_values.map((val) => {
                        if (val.table_descriptor_id === bulkEditPayload.type && obj.is_selected === true) {
                            let inputType = isSet(bulkEditPayload.inputType, "");
                            
                            if (inputType === 'Files') {
                                val.files = fieldValue;
                            } else if (inputType === "Reminder") {
                                val.reminder = fieldValue;
                            } else if (inputType === 'currencyType') {
                                val.currency_code = isSet(fieldValue.selectedValue, fieldValue);
                            } else {
                                val.selected_value = fieldValue;
                                val.overridden = true;
                            }
                        }
                        return val;
                    })
            };
        });
        
        const updatedSubformData = [...subformData];
        updatedSubformData[subformIndex] = {
            ...updatedSubformData[subformIndex],
            subform_rows: updateddata
        };
        
        if (setSubFormDataAction) {
            setSubFormDataAction(modType, updatedSubformData);
        }
        
        if (closeBulkEditPopup) {
            closeBulkEditPopup();
        }
    };
};

/**
 * Factory function to create select data handler
 * @param {Object} params - Handler parameters
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {String} params.modType - Module type
 * @param {Function} params.setSubFormDataAction - Redux action
 * @returns {Function} Select data handler
 */
export const createSelectDataHandler = ({ subformData, subformIndex, modType, setSubFormDataAction }) => {
    return (ind, val) => {
        if (subformData && subformData[subformIndex] && subformData[subformIndex].subform_rows[ind] !== undefined) {
            // Create a new copy to avoid mutation
            const updatedSubformData = [...subformData];
            updatedSubformData[subformIndex] = {
                ...updatedSubformData[subformIndex],
                subform_rows: updatedSubformData[subformIndex].subform_rows.map((row, rowIdx) => {
                    if (rowIdx === ind) {
                        return { ...row, is_selected: val };
                    }
                    return row;
                })
            };
            
            if (setSubFormDataAction) {
                setSubFormDataAction(modType, updatedSubformData);
            }
        }
    };
};

/**
 * Factory function to create select all handler
 * @param {Object} params - Handler parameters
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {String} params.modType - Module type
 * @param {Function} params.setSubFormDataAction - Redux action
 * @returns {Function} Select all handler
 */
export const createSelectAllHandler = ({ subformData, subformIndex, modType, setSubFormDataAction }) => {
    return (val) => {
        if (setSubFormDataAction && subformData && subformData[subformIndex]) {
            const updatedSubformData = [...subformData];
            updatedSubformData[subformIndex] = {
                ...updatedSubformData[subformIndex],
                subform_rows: updatedSubformData[subformIndex].subform_rows.map((item) => {
                    return { ...item, is_selected: val };
                })
            };
            setSubFormDataAction(modType, updatedSubformData);
        }
    };
};

/**
 * Factory function to create remove subform handler
 * @param {Object} params - Handler parameters
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {Number} params.rowIndex - Row index
 * @param {String} params.pageType - Page type (view/form)
 * @param {String} params.modType - Module type
 * @param {Function} params.setSubFormDataAction - Redux action
 * @param {Function} params.clearSubformValidation - Clear validation function
 * @param {Function} params.removeSubformDataHandler - Remove handler for view mode
 * @returns {Function} Remove subform handler
 */
export const createRemoveSubFormsHandler = ({ 
    subformData, 
    subformIndex, 
    rowIndex, 
    pageType, 
    modType, 
    setSubFormDataAction, 
    clearSubformValidation,
    removeSubformDataHandler
}) => {
    return async () => {
        let display_postition = 0;
        if (pageType === "view") {
            if (removeSubformDataHandler) {
                removeSubformDataHandler(subformIndex, [
                    subformData[subformIndex].subform_rows[rowIndex].id,
                ]);
            }
        } else if (pageType === "form") {
            let subformDataRows = subformData[subformIndex].subform_rows.filter(
                (filter, filIndex) => {
                    if (filIndex !== rowIndex) {
                        filter.display_postition = display_postition;
                        display_postition++;
                        return filter;
                    }
                }
            );
            const updatedSubformData = [...subformData];
            updatedSubformData[subformIndex] = {
                ...updatedSubformData[subformIndex],
                subform_rows: subformDataRows
            };
            
            if (setSubFormDataAction) {
                await setSubFormDataAction(modType, updatedSubformData);
            }
            if (clearSubformValidation) {
                clearSubformValidation(modType, rowIndex);
            }
        }
    };
};

/**
 * Factory function to create cancel subform handler
 * @param {Object} params - Handler parameters
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {Number} params.rowIndex - Row index
 * @param {String} params.modType - Module type
 * @param {Function} params.setSubFormDataAction - Redux action
 * @returns {Function} Cancel subform handler
 */
export const createCancelSubFormsHandler = ({ subformData, subformIndex, rowIndex, modType, setSubFormDataAction }) => {
    return () => {
        const updatedSubformData = [...subformData];
        updatedSubformData[subformIndex] = {
            ...updatedSubformData[subformIndex],
            subform_rows: updatedSubformData[subformIndex].subform_rows.map((row, idx) => {
                if (idx === rowIndex) {
                    return { ...row, isOpenBox: false };
                }
                return row;
            })
        };
        
        // If row has no ID, remove it
        if (isSet(updatedSubformData[subformIndex].subform_rows[rowIndex].id, null) === null) {
            let display_postition = 0;
            const filteredRows = updatedSubformData[subformIndex].subform_rows.filter((filter, filIndex) => {
                if (filIndex !== rowIndex) {
                    filter.display_postition = display_postition;
                    display_postition++;
                    return filter;
                }
            });
            updatedSubformData[subformIndex] = {
                ...updatedSubformData[subformIndex],
                subform_rows: filteredRows
            };
        }
        
        if (setSubFormDataAction) {
            setSubFormDataAction(modType, updatedSubformData);
        }
    };
};

/**
 * Factory function to create edit subform handler
 * @param {Object} params - Handler parameters
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {Number} params.rowIndex - Row index
 * @param {String} params.modType - Module type
 * @param {Function} params.setSubFormDataAction - Redux action
 * @returns {Function} Edit subform handler
 */
export const createEditSubFormsHandler = ({ subformData, subformIndex, rowIndex, modType, setSubFormDataAction }) => {
    return () => {
        const updatedSubformData = [...subformData];
        updatedSubformData[subformIndex] = {
            ...updatedSubformData[subformIndex],
            subform_rows: updatedSubformData[subformIndex].subform_rows.map((row, idx) => {
                if (idx === rowIndex) {
                    return { ...row, isOpenBox: true };
                }
                return row;
            })
        };
        
        if (setSubFormDataAction) {
            setSubFormDataAction(modType, updatedSubformData);
        }
    };
};

/**
 * Factory function to create save subform handler
 * @param {Object} params - Handler parameters
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {Number} params.rowIndex - Row index
 * @param {Function} params.saveAndUpdateSubform - Save and update function
 * @returns {Function} Save subform handler
 */
export const createSaveSubFormsHandler = ({ subformData, subformIndex, rowIndex, saveAndUpdateSubform }) => {
    return () => {
        const updatedSubformData = [...subformData];
        updatedSubformData[subformIndex] = {
            ...updatedSubformData[subformIndex],
            subform_rows: updatedSubformData[subformIndex].subform_rows.map((row, idx) => {
                if (idx === rowIndex) {
                    return { ...row, isOpenBox: false };
                }
                return row;
            })
        };
        
        if (saveAndUpdateSubform) {
            saveAndUpdateSubform(rowIndex, true);
        }
    };
};

/**
 * Factory function to create copy subform handler
 * @param {Object} params - Handler parameters
 * @param {Array} params.subformData - Subform data array
 * @param {Number} params.subformIndex - Subform index
 * @param {Number} params.rowIndex - Row index
 * @param {String} params.modType - Module type
 * @param {Function} params.setSubFormDataAction - Redux action
 * @returns {Function} Copy subform handler
 */
export const createCopySubFormsHandler = ({ subformData, subformIndex, rowIndex, modType, setSubFormDataAction }) => {
    return () => {
        const sourceRow = subformData[subformIndex].subform_rows[rowIndex];
        const subformDataRows = sourceRow.subform_column_values;
        
        const newRow = {
            id: "",
            display_postition: subformData[subformIndex].subform_rows.length,
            is_selected: false,
            isOpenBox: true,
            subform_column_values: subformDataRows.map((item) => {
                return {
                    dpnm: item?.dpnm ?? '',
                    table_descriptor_id: item.table_descriptor_id,
                    display_name: item.display_name,
                    selected_value: item.selected_value,
                };
            }),
        };
        
        const updatedSubformData = [...subformData];
        updatedSubformData[subformIndex] = {
            ...updatedSubformData[subformIndex],
            subform_rows: [...updatedSubformData[subformIndex].subform_rows, newRow]
        };
        
        if (setSubFormDataAction) {
            setSubFormDataAction(modType, updatedSubformData);
        }
    };
};

